import type { MeetingId, RaceId, RaceView } from '@ps/isp-web-services-types';
import type { Selector } from 'react-redux';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { isMeetingBasedQuickSwitcherData } from '@swf/sports/utils/quickSwitcherData';

import type { QuickSwitcherState } from './types';

interface ThisType {
  races: Selector<RootState, Record<string, RaceView> | undefined>;
  firstOffRaceId: Selector<RootState, RaceId | undefined>;
}

export const selectors = (slice: Slicer<QuickSwitcherState>, createSelector: SelectorCreator) => ({
  isLoading() {
    return slice((state) => state.isLoading);
  },
  data() {
    return slice((state) => state.data);
  },
  isNavigating() {
    return slice((state) => state.isNavigating);
  },
  races() {
    return createSelector(
      slice,
      (_state: RootState, raceIds: RaceId[] | undefined) => raceIds,
      (state, raceIds) =>
        isMeetingBasedQuickSwitcherData(state.data)
          ? Array.isArray(raceIds)
            ? Object.fromEntries(
                [...Object.values(state.data.races).filter((race) => raceIds.includes(race.raceId))]
                  .sort(
                    (raceA, raceB) => raceIds.indexOf(raceA.raceId) - raceIds.indexOf(raceB.raceId)
                  )
                  .map((race) => [race.raceId, race])
              )
            : state.data.races
          : undefined
    );
  },
  eventType() {
    return createSelector(
      slice,
      (_state: RootState, eventTypeId: EventTypeId | undefined) => eventTypeId,
      (state, eventTypeId) =>
        isMeetingBasedQuickSwitcherData(state.data) && typeof eventTypeId !== 'undefined'
          ? state.data?.eventTypes[eventTypeId]
          : undefined
    );
  },
  meeting() {
    return createSelector(
      slice,
      (_state: RootState, meetingId: MeetingId | undefined) => meetingId,
      (state, meetingId) =>
        isMeetingBasedQuickSwitcherData(state.data) && typeof meetingId !== 'undefined'
          ? state.data.meetings[meetingId]
          : undefined
    );
  },
  firstOffRaceId() {
    return slice((state) =>
      isMeetingBasedQuickSwitcherData(state.data) ? state.data.firstOffRaceId : undefined
    );
  },
  firstOffRace(this: ThisType) {
    return createSelector(this.firstOffRaceId, this.races, (firstOffRaceId, races) =>
      firstOffRaceId && races ? races[firstOffRaceId] : undefined
    );
  }
});
